import { useTranslation } from 'next-i18next';
import { ReactNode, memo, useEffect, useState } from 'react';
import { IEverythingToKnowContent } from '@bridebook/toolbox/src/seo-facets/search-widgets';
import { SearchType } from '@bridebook/toolbox/src/search-suppliers/types';
import { cn } from '@bridebook/aura';
import { ILocationData } from '@bridebook/web/app-shared/lib/search/types';
import { renderHtml } from '@bridebook/web/app-shared/lib/ui/html-renderer';

interface IProps {
  everythingToKnow: IEverythingToKnowContent[];
  locationData?: ILocationData | null;
}

const WIDGET_ALLOWED_TAGS = '<h3><p><ul><ol><li><strong><b><em><u><a>';

const renderColumns = (tab: IEverythingToKnowContent): ReactNode => (
  <>
    <div>
      {renderHtml(tab.column1 || '', { allowedTags: WIDGET_ALLOWED_TAGS, cleanLinks: true })}
    </div>
    <div>
      {renderHtml(tab.column2 || '', { allowedTags: WIDGET_ALLOWED_TAGS, cleanLinks: true })}
    </div>
  </>
);

const EverythingToKnow = ({ everythingToKnow, locationData }: IProps) => {
  const [mounted, setMounted] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { t } = useTranslation('search');

  useEffect(() => {
    setMounted(true);
    setActiveTabIndex(0);
  }, [everythingToKnow]);

  if (!locationData || !everythingToKnow) {
    return null;
  }

  const location =
    locationData.type === SearchType.County
      ? locationData.county || locationData.country
      : locationData.town || locationData.name;

  const title = <h5 className="bsetk-title">{t('everythingToKnow.title', { location })}</h5>;

  if (!mounted) {
    return (
      <div className="bsetk">
        {title}
        {everythingToKnow.map((tab, i) => (
          <div key={i} className="bsetk-ssr-section">
            <h3 className="bsetk-ssr-title">{tab.title}</h3>
            <div className="bsetk-content">{renderColumns(tab)}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bsetk">
      {title}
      <div className="bsetk-tabs">
        {everythingToKnow.map((tab, i) => (
          <a
            key={i}
            role="tab"
            aria-selected={i === activeTabIndex}
            onClick={() => setActiveTabIndex(i)}
            className={cn('bsetk-tab', i === activeTabIndex && 'bsetk-tab-active')}>
            {tab.title}
          </a>
        ))}
      </div>
      <div className="bsetk-box">
        {everythingToKnow.map((tab, i) => (
          <div key={i} className={i === activeTabIndex ? 'bsetk-content' : undefined}>
            {renderColumns(tab)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(EverythingToKnow);
